import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import ConsultingBreadcrumbs from "@/components/ConsultingBreadcrumbs";
import { renderRich } from "@/lib/renderRich";
import {
  ArrowLeft,
  Download,
  CheckCircle2,
  Circle,
  Loader2,
  RefreshCw,
  Filter,
  StickyNote,
  Share2,
  X,
  Mail,
  Link2,
  Send,
  Copy,
  ShieldCheck,
  Search,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AREAS = [
  { code: "A", label: "Marca", color: "#0F6E56" },
  { code: "B", label: "Selección", color: "#0891b2" },
  { code: "C", label: "Onboarding", color: "#7c3aed" },
  { code: "D", label: "Evaluación", color: "#ea580c" },
  { code: "E", label: "Feedback", color: "#dc2626" },
  { code: "F", label: "Desarrollo", color: "#0369a1" },
  { code: "G", label: "Global", color: "#525252" },
];
const AREA_MAP = Object.fromEntries(AREAS.map((a) => [a.code, a]));
const HORIZONTES = ["30d", "90d", "180d"];
const HORIZ_LABELS = { "30d": "30 días", "90d": "90 días", "180d": "180 días" };

export default function ConsultingChecklist() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState({ total: 0, done: 0, pct: 0 });
  const [test, setTest] = useState(null);
  const [filterArea, setFilterArea] = useState(() => {
    const a = new URLSearchParams(window.location.search).get("area");
    return AREA_MAP[a] ? a : "all";
  });
  const [filterHoriz, setFilterHoriz] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [search, setSearch] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareEmails, setShareEmails] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [shareSending, setShareSending] = useState(false);
  const [shareLoadingUrl, setShareLoadingUrl] = useState(false);
  const lastPctRef = useRef(-1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // iter89 · E8 · Una sola llamada. El endpoint ya devuelve `test_meta`
      // con los metadatos mínimos (company_name + report_ready_at). Antes
      // disparábamos una 2ª llamada paralela a `/tests/{id}` que arrastraba
      // los 7 bloques markdown (~200 KB) y hacía la página 1-3 s más lenta.
      const r = await api.get(`/consulting/tests/${testId}/checklist`);
      setItems(r.data.items || []);
      setProgress(r.data.progress || { total: 0, done: 0, pct: 0 });
      setTest(r.data.test_meta || null);
      lastPctRef.current = r.data.progress?.pct ?? 0;
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("El informe aún no está listo");
        navigate(`/consulting/${testId}/report`, { replace: true });
      } else {
        toast.error("No pudimos cargar el checklist");
      }
    } finally {
      setLoading(false);
    }
  }, [testId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  function fireCelebration(pct) {
    const milestones = [25, 50, 75, 100];
    for (const m of milestones) {
      if (lastPctRef.current < m && pct >= m) {
        // Confetti dorado + verde
        confetti({
          particleCount: m === 100 ? 220 : 120,
          spread: 80,
          origin: { y: 0.6 },
          colors: ["#C9A84C", "#E5C874", "#0F6E56", "#0b3d2e"],
          scalar: 1.05,
        });
        if (m === 100) {
          toast.success("🎉 Checklist completado", {
            description: "Todas las acciones marcadas. ¿Descargar el PDF final o compartir con tu equipo?",
            duration: 8000,
            action: { label: "Descargar PDF", onClick: () => downloadPdf() },
          });
        } else {
          toast.success(`¡${m}% completado!`, { description: "Sigue así · el momentum está de tu lado.", duration: 4500 });
        }
      }
    }
    lastPctRef.current = pct;
  }

  async function toggleDone(item) {
    const newDone = !item.done;
    const previous = [...items];
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, done: newDone, completed_at: newDone ? new Date().toISOString() : null } : i)));
    try {
      const r = await api.patch(`/consulting/tests/${testId}/checklist/${item.id}`, { done: newDone });
      setItems((prev) => prev.map((i) => (i.id === item.id ? r.data.item : i)));
      setProgress(r.data.progress);
      if (newDone && r.data.progress?.pct !== undefined) {
        fireCelebration(r.data.progress.pct);
      } else if (!newDone) {
        lastPctRef.current = r.data.progress?.pct ?? 0;
      }
    } catch {
      setItems(previous);
      toast.error("No pudimos actualizar el ítem");
    }
  }

  async function updateNotes(item, text) {
    const previous = [...items];
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, notes: text } : i)));
    try {
      await api.patch(`/consulting/tests/${testId}/checklist/${item.id}`, { notes: text });
    } catch {
      setItems(previous);
      toast.error("No pudimos guardar la nota");
    }
  }

  async function regenerate() {
    setRegenOpen(false);
    setRegenerating(true);
    try {
      const r = await api.post(`/consulting/tests/${testId}/checklist/regenerate`);
      setItems(r.data.items || []);
      setProgress(r.data.progress || { total: 0, done: 0, pct: 0 });
      lastPctRef.current = r.data.progress?.pct ?? 0;
      toast.success("Checklist regenerado");
    } catch {
      toast.error("No pudimos regenerar el checklist");
    } finally {
      setRegenerating(false);
    }
  }

  async function openShare() {
    setShareOpen(true);
    if (shareUrl) return;
    setShareLoadingUrl(true);
    try {
      const { data } = await api.post(`/consulting/tests/${testId}/share`);
      // El share_token es el mismo para informe y checklist. Añadimos el
      // sufijo `/checklist` para dirigir la vista pública a la checklist,
      // no al informe general.
      const base = (data.public_url || "").replace(/\/$/, "");
      setShareUrl(base ? `${base}/checklist` : "");
    } catch {
      toast.error("No pudimos generar el link");
    } finally {
      setShareLoadingUrl(false);
    }
  }

  function copyShareUrl() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("Link copiado"),
      () => toast.error("No pudimos copiar")
    );
  }

  async function sendShareEmail() {
    const emails = shareEmails.split(/[,;\n\s]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) return toast.error("Añade al menos un email");
    if (emails.length > 5) return toast.error("Máximo 5 emails");
    setShareSending(true);
    try {
      const { data } = await api.post(`/consulting/tests/${testId}/share/email`, {
        emails,
        message: shareMessage || null,
      });
      const sent = (data.sent || []).length;
      const failed = (data.failed || []).length;
      if (sent > 0) toast.success(`Enviado a ${sent} destinatario${sent > 1 ? "s" : ""}`);
      if (failed > 0) toast.error(`Fallaron ${failed}`);
      if (sent > 0 && failed === 0) {
        setShareEmails("");
        setShareMessage("");
        setShareOpen(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar los emails");
    } finally {
      setShareSending(false);
    }
  }

  async function downloadPdf() {
    try {
      await downloadPdfWithProgress({
        url: `/consulting/tests/${testId}/checklist/pdf`,
        filename: `Checklist_${new Date().toISOString().slice(0, 10)}.pdf`,
        kind: "consulting-checklist",
        label: "Preparando tu plan de acción",
        variant: "premium",
        timeout: 180000,
      });
    } catch {
      toast.error("No pudimos descargar el PDF");
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filterArea !== "all" && i.area !== filterArea) return false;
      if (filterHoriz !== "all" && i.horizonte !== filterHoriz) return false;
      if (filterState === "pending" && i.done) return false;
      if (filterState === "done" && !i.done) return false;
      if (filterState === "tablero" && !i.tablero) return false;
      if (q) {
        const hay = ((i.accion || "") + " " + (i.indicador || "") + " " + (i.notes || "")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filterArea, filterHoriz, filterState, search]);

  const byHoriz = useMemo(() => {
    const g = { "30d": [], "90d": [], "180d": [] };
    filtered.forEach((i) => {
      if (!g[i.horizonte]) g[i.horizonte] = [];
      g[i.horizonte].push(i);
    });
    return g;
  }, [filtered]);

  // Progreso por área (para las 7 mini-barras)
  const areaProgress = useMemo(() => {
    const totals = {};
    const dones = {};
    items.forEach((i) => {
      totals[i.area] = (totals[i.area] || 0) + 1;
      if (i.done) dones[i.area] = (dones[i.area] || 0) + 1;
    });
    return AREAS.map((a) => {
      const total = totals[a.code] || 0;
      const done = dones[a.code] || 0;
      return { ...a, total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
    });
  }, [items]);

  // Progreso por horizonte con porcentaje
  const horizProgress = useMemo(() => {
    const totals = {};
    const dones = {};
    items.forEach((i) => {
      totals[i.horizonte] = (totals[i.horizonte] || 0) + 1;
      if (i.done) dones[i.horizonte] = (dones[i.horizonte] || 0) + 1;
    });
    return Object.fromEntries(
      HORIZONTES.map((h) => {
        const total = totals[h] || 0;
        const done = dones[h] || 0;
        return [h, { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 }];
      })
    );
  }, [items]);

  // Última acción completada
  const lastActivity = useMemo(() => {
    const done = items.filter((i) => i.done && i.completed_at);
    if (done.length === 0) return null;
    return done.reduce((max, i) => (i.completed_at > (max?.completed_at || "") ? i : max), null);
  }, [items]);

  function timeAgoShort(iso) {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.round(hours / 24);
    if (days === 1) return "ayer";
    return `hace ${days} días`;
  }

  if (loading) {
    return (
      <div
        className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
        style={{
          background:
            "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
            "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
            "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
        }}
        data-testid="consulting-checklist-skeleton"
      >
        <div className="max-w-4xl mx-auto">
          <div className="skeleton-pulse h-4 w-32 rounded mb-8" />
          <div
            className="rounded-3xl p-8 mb-6 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
          >
            <div className="skeleton-pulse h-3 w-40 mb-4 rounded" style={{ background: "rgba(201,168,76,0.15)" }} />
            <div className="skeleton-pulse h-9 w-72 rounded mb-4" style={{ background: "rgba(255,255,255,0.12)" }} />
            <div className="skeleton-pulse h-3 w-full rounded" style={{ background: "rgba(255,255,255,0.08)" }} />
          </div>
          <div className="rounded-2xl p-4 mb-6 bg-white">
            <div className="skeleton-pulse h-8 w-full rounded" />
          </div>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="rounded-2xl p-5 mb-3 bg-white flex items-start gap-3">
              <div className="skeleton-pulse w-6 h-6 rounded-full" />
              <div className="flex-1">
                <div className="skeleton-pulse h-3 w-24 rounded mb-2" />
                <div className="skeleton-pulse h-4 w-full rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
          "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
          "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
      }}
      data-testid="consulting-checklist"
    >
      <div className="max-w-4xl mx-auto">
        <ConsultingBreadcrumbs
          crumbs={[
            { label: "Consultorías", to: "/consulting" },
            { label: test?.nickname || test?.company_name || "Consultoría", to: `/consulting/${testId}/report` },
            { label: "Plan de acción" },
          ]}
        />
        <button
          onClick={() => navigate(`/consulting/${testId}/report`)}
          className="text-xs font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-8 transition-colors group"
          data-testid="consulting-checklist-back"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" /> Volver al informe
        </button>

        {/* Header + Progress premium */}
        <div
          className="relative rounded-3xl p-8 mb-8 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
            boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset",
          }}
        >
          <span
            aria-hidden
            className="absolute -top-8 -right-8 pointer-events-none"
            style={{
              width: 220,
              height: 220,
              background:
                "radial-gradient(circle at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(201,168,76,0) 65%)," +
                "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)",
            }}
          />
          <span
            aria-hidden
            className="absolute bottom-0 left-0 right-0 h-1"
            style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
          />
          <div className="relative flex items-start justify-between gap-4 flex-wrap mb-5">
            <div className="min-w-0 w-full lg:flex-1 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                Checklist de implementación
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight" style={{ letterSpacing: "-0.02em" }}>
                {progress.done} <span style={{ color: "rgba(255,255,255,0.4)" }}>de</span> {progress.total} completadas
                {test?.company_name && (
                  <>
                    {" · "}
                    <span
                      style={{
                        background: "linear-gradient(90deg, #E5C874 0%, #C9A84C 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {test.company_name}
                    </span>
                  </>
                )}
              </h1>
              <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.75)" }}>
                Marca cada acción según la vayas ejecutando. Puedes añadir notas por ítem.
              </p>
              {(test?.report_ready_at || lastActivity) && (
                <div className="mt-4 pt-3 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ borderTop: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>
                  {test?.report_ready_at && (
                    <>
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" style={{ color: "#C9A84C" }} />
                        Basado en tu informe del {new Date(test.report_ready_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                      <span className="text-white/25">·</span>
                      <span>Consultoría OptimizaRRHH</span>
                    </>
                  )}
                  {lastActivity && (
                    <>
                      <span className="text-white/25">·</span>
                      <span style={{ color: "#E5C874" }}>Última acción: {timeAgoShort(lastActivity.completed_at)}</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap items-center print-hide order-1 lg:order-2 w-full lg:w-auto justify-end">
              <button
                onClick={() => setRegenOpen(true)}
                disabled={regenerating}
                className="rounded-full px-4 md:px-5 py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                style={{
                  background: "rgba(255,255,255,0.08)",
                  color: "#ffffff",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(201,168,76,0.35)",
                }}
                data-testid="consulting-checklist-regenerate"
              >
                <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">Regenerar</span>
              </button>
              <button
                onClick={openShare}
                className="rounded-full px-4 md:px-5 py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
                style={{
                  background: "rgba(255,255,255,0.08)",
                  color: "#ffffff",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(201,168,76,0.35)",
                }}
                data-testid="consulting-checklist-share"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Compartir</span>
              </button>
              <button
                onClick={downloadPdf}
                className="group relative rounded-full px-4 md:px-6 py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5 overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                  color: "#0b3d2e",
                  boxShadow: "0 8px 22px -6px rgba(201,168,76,0.55)",
                }}
                data-testid="consulting-checklist-download-pdf"
              >
                <Download className="w-4 h-4" strokeWidth={2.5} /> PDF
              </button>
            </div>
          </div>

          {/* Progress bar dorada global */}
          <div
            className="relative w-full h-3 rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.12)" }}
            data-testid="consulting-checklist-progress-bar"
          >
            <div
              className="h-full transition-all duration-700"
              style={{
                width: `${progress.pct}%`,
                background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                boxShadow: "0 0 10px rgba(201,168,76,0.55)",
              }}
            />
          </div>
          <div className="relative text-xs mt-2 font-semibold" style={{ color: "#E5C874" }}>{progress.pct}% completado</div>

          {/* Progreso por área · 7 mini-barras */}
          {items.length > 0 && (
            <div className="relative mt-5 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }} data-testid="consulting-checklist-area-progress">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>Progreso por área</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
                {areaProgress.map((a) => {
                  const active = filterArea === a.code;
                  return (
                    <button
                      key={a.code}
                      onClick={() => setFilterArea(active ? "all" : a.code)}
                      className="text-left rounded-xl px-3 py-2 transition-all hover:-translate-y-0.5"
                      style={{
                        background: active ? "rgba(201,168,76,0.20)" : "rgba(255,255,255,0.05)",
                        border: active ? "1px solid rgba(201,168,76,0.55)" : "1px solid rgba(255,255,255,0.08)",
                      }}
                      data-testid={`consulting-checklist-area-progress-${a.code}`}
                      title={`${a.label}: ${a.done}/${a.total}`}
                    >
                      <div className="flex items-center justify-between mb-1.5 gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className="text-[10px] font-bold uppercase tracking-widest flex-shrink-0"
                            style={{ color: a.pct === 100 ? "#E5C874" : "#ffffff" }}
                          >
                            {a.code}
                          </span>
                          <span
                            className="text-[10px] font-semibold truncate"
                            style={{ color: "rgba(255,255,255,0.75)" }}
                          >
                            {a.label}
                          </span>
                        </div>
                        <span className="text-[10px] font-bold tabular-nums flex-shrink-0" style={{ color: a.total > 0 ? (a.pct === 100 ? "#E5C874" : "rgba(255,255,255,0.75)") : "rgba(255,255,255,0.3)" }}>
                          {a.done}/{a.total}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.12)" }}>
                        <div className="h-full transition-all duration-500" style={{ width: `${a.pct}%`, background: a.pct === 100 ? "linear-gradient(90deg, #C9A84C 0%, #E5C874 100%)" : a.color, boxShadow: a.pct === 100 ? "0 0 8px rgba(201,168,76,0.6)" : undefined }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Filtros premium + búsqueda */}
        <div
          className="rounded-2xl p-4 mb-8 flex flex-wrap gap-3 items-center print-hide"
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
            boxShadow: "0 10px 30px -18px rgba(11,61,46,0.15), 0 0 0 1px rgba(11,61,46,0.06)",
          }}
        >
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#94a3b8" }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar acción, indicador o nota…"
              className="w-full text-sm bg-white rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40 transition"
              style={{ border: "1px solid #e2e8f0" }}
              data-testid="consulting-checklist-search"
            />
          </div>
          <Filter className="w-4 h-4 flex-shrink-0" style={{ color: "#0F6E56" }} />
          <select
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            className="text-sm bg-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40 transition"
            style={{ border: "1px solid #e2e8f0" }}
            data-testid="consulting-checklist-filter-area"
          >
            <option value="all">Todas las áreas</option>
            {AREAS.map((a) => (
              <option key={a.code} value={a.code}>{a.code} · {a.label}</option>
            ))}
          </select>
          <select
            value={filterHoriz}
            onChange={(e) => setFilterHoriz(e.target.value)}
            className="text-sm bg-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40 transition"
            style={{ border: "1px solid #e2e8f0" }}
            data-testid="consulting-checklist-filter-horizonte"
          >
            <option value="all">Todos los horizontes</option>
            {HORIZONTES.map((h) => (
              <option key={h} value={h}>{HORIZ_LABELS[h]}</option>
            ))}
          </select>
          <select
            value={filterState}
            onChange={(e) => setFilterState(e.target.value)}
            className="text-sm bg-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40 transition"
            style={{ border: "1px solid #e2e8f0" }}
            data-testid="consulting-checklist-filter-state"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="done">Completadas</option>
            {items.some((i) => i.tablero) && <option value="tablero">Del tablero</option>}
          </select>
          <div className="text-xs text-slate-500 ml-auto font-medium">Mostrando <span className="text-slate-900 font-bold">{filtered.length}</span> de {items.length}</div>
        </div>

        {/* Grupos por horizonte premium */}
        {HORIZONTES.map((h) => {
          const list = byHoriz[h] || [];
          if (!list.length) return null;
          const hp = horizProgress[h] || { total: 0, done: 0, pct: 0 };
          return (
            <div key={h} className="mb-8" data-testid={`consulting-checklist-group-${h}`}>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div
                  className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] px-3 py-1.5 rounded-full"
                  style={{
                    background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)",
                    color: "#E5C874",
                    boxShadow: "0 4px 12px -4px rgba(11,61,46,0.35)",
                  }}
                >
                  <span style={{ width: 5, height: 5, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                  Horizonte {HORIZ_LABELS[h]}
                </div>
                <div
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full tabular-nums"
                  style={{
                    background: hp.pct === 100 ? "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)" : "rgba(15,110,86,0.10)",
                    color: hp.pct === 100 ? "#0b3d2e" : "#0F6E56",
                    border: hp.pct === 100 ? "0.5px solid rgba(11,61,46,0.35)" : "0.5px solid rgba(15,110,86,0.22)",
                  }}
                >
                  {hp.pct === 100 && <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />}
                  {hp.done}/{hp.total} · {hp.pct}%
                </div>
                <span style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(15,110,86,0.15), transparent)" }} />
              </div>
              <div className="space-y-3">
                {list.map((it) => (
                  <ChecklistRow
                    key={it.id}
                    item={it}
                    open={!!notesOpen[it.id]}
                    onToggleDone={() => toggleDone(it)}
                    onToggleNotes={() => setNotesOpen((s) => ({ ...s, [it.id]: !s[it.id] }))}
                    onUpdateNotes={(t) => updateNotes(it, t)}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div
            className="rounded-3xl p-12 text-center text-slate-400"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
              boxShadow: "0 10px 30px -18px rgba(11,61,46,0.15), 0 0 0 1px rgba(11,61,46,0.06)",
            }}
            data-testid="consulting-checklist-empty"
          >
            No hay acciones que coincidan con los filtros actuales.
          </div>
        )}
      </div>

      {/* AlertDialog regenerar */}
      <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>
        <AlertDialogContent data-testid="consulting-checklist-regenerate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerar el checklist</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción borrará los estados <strong>completados</strong> y las <strong>notas</strong> de cada ítem, y generará una nueva lista basada en tu informe actual. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="consulting-checklist-regenerate-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={regenerate}
              className="bg-[#0b3d2e] hover:bg-[#0F6E56]"
              data-testid="consulting-checklist-regenerate-confirm"
            >
              Sí, regenerar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Share */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setShareOpen(false)}
          data-testid="consulting-checklist-share-modal"
        >
          <div
            className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 30px 60px -20px rgba(11,61,46,0.45), 0 0 0 1px rgba(201,168,76,0.15)" }}
          >
            <div className="relative px-6 py-5 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}>
              <span aria-hidden className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#E5C874" }}>Compartir checklist</div>
                <div className="text-white font-semibold text-sm mt-0.5 truncate">
                  {test?.company_name || "Consultoría"} · {progress.done}/{progress.total} completadas
                </div>
              </div>
              <button onClick={() => setShareOpen(false)} className="rounded-full p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition" aria-label="Cerrar" data-testid="consulting-checklist-share-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                  <Link2 className="w-3 h-3" /> Link público (solo-lectura)
                </label>
                {shareLoadingUrl ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Generando link…
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs md:text-sm text-slate-700 font-mono"
                      onClick={(e) => e.target.select()}
                      data-testid="consulting-checklist-share-url"
                    />
                    <button onClick={copyShareUrl} className="rounded-xl px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 transition" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", color: "#ffffff" }} data-testid="consulting-checklist-share-copy">
                      <Copy className="w-3.5 h-3.5" /> Copiar
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  Tu equipo verá el informe completo (checklist incluido) en modo solo-lectura.
                </p>
              </div>

              <div className="h-px bg-slate-100" />

              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                  <Mail className="w-3 h-3" /> O envíalo por email (máx 5)
                </label>
                <input
                  type="text"
                  placeholder="ana@empresa.com, socio@empresa.com"
                  value={shareEmails}
                  onChange={(e) => setShareEmails(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15"
                  data-testid="consulting-checklist-share-emails"
                />
                <textarea
                  placeholder="Mensaje opcional…"
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  rows={2}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15 resize-none"
                  data-testid="consulting-checklist-share-message"
                />
                <button
                  onClick={sendShareEmail}
                  disabled={shareSending || !shareUrl}
                  className="mt-3 w-full rounded-full py-2.5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                  style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)", boxShadow: "0 8px 22px -8px rgba(11,61,46,0.5)" }}
                  data-testid="consulting-checklist-share-send"
                >
                  {shareSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {shareSending ? "Enviando…" : "Enviar por email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChecklistRow({ item, open, onToggleDone, onToggleNotes, onUpdateNotes }) {
  const area = AREA_MAP[item.area] || AREA_MAP.G;
  const [draft, setDraft] = useState(item.notes || "");
  useEffect(() => {
    setDraft(item.notes || "");
  }, [item.notes]);

  return (
    <div
      className="relative rounded-2xl p-5 flex items-start gap-4 transition-all overflow-hidden"
      style={{
        background: item.done
          ? "linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
        boxShadow: item.done
          ? "0 10px 30px -18px rgba(15,110,86,0.25), 0 0 0 1px rgba(15,110,86,0.15)"
          : "0 10px 30px -18px rgba(11,61,46,0.15), 0 0 0 1px rgba(11,61,46,0.06)",
      }}
      data-testid={`consulting-checklist-item-${item.id}`}
    >
      {/* Franja lateral con color del área */}
      <span
        aria-hidden
        className="absolute top-0 bottom-0 left-0 w-1"
        style={{ background: `linear-gradient(180deg, ${area.color} 0%, ${area.color}80 100%)` }}
      />
      <button
        onClick={onToggleDone}
        className="flex-shrink-0 mt-0.5 transition-transform hover:scale-110"
        aria-label={item.done ? "Marcar como pendiente" : "Marcar como hecho"}
        data-testid="consulting-checklist-item-toggle"
      >
        {item.done ? (
          <CheckCircle2 className="w-6 h-6" style={{ color: "#0F6E56" }} strokeWidth={2.25} />
        ) : (
          <Circle className="w-6 h-6 text-slate-300 hover:text-emerald-500 transition-colors" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <span
            className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${area.color} 0%, ${area.color}CC 100%)`,
              boxShadow: `0 3px 8px -2px ${area.color}66`,
            }}
          >
            {area.code} · {area.label}
          </span>
          {item.tablero && (
            <span className="inline-block px-2 py-1 rounded-md text-[10px] font-bold flex-shrink-0" style={{ background: "#EAF3EC", color: "#0F6E56", border: "1px solid #0F6E56" }} data-testid="consulting-checklist-item-tablero-tag">
              {item.tablero.label}
            </span>
          )}
          <p className={`text-sm leading-relaxed ${item.done ? "text-slate-500 line-through" : "text-slate-900 font-medium"}`}>
            {renderRich(item.accion)}
          </p>
        </div>
        {item.indicador && (
          <div className="text-xs text-slate-600 mt-3 pl-3 py-1" style={{ borderLeft: "2px solid #C9A84C" }}>
            <span className="font-bold uppercase tracking-widest text-[10px]" style={{ color: "#0F6E56" }}>Indicador · </span>
            {renderRich(item.indicador)}
          </div>
        )}
        {item.done && item.completed_at && (
          <div className="text-[11px] font-semibold mt-2" style={{ color: "#0F6E56" }}>
            ✓ Completado el {new Date(item.completed_at).toLocaleDateString("es-ES")}
          </div>
        )}
        <div className="mt-3">
          <button
            onClick={onToggleNotes}
            className="text-xs font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 transition-colors"
            data-testid="consulting-checklist-item-notes-toggle"
          >
            <StickyNote className="w-3.5 h-3.5" /> {item.notes ? "Ver/editar nota" : "Añadir nota"}
          </button>
          {open && (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => draft !== item.notes && onUpdateNotes(draft)}
              placeholder="Notas internas (se guarda al salir del campo)…"
              className="w-full mt-2 text-sm bg-white rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40 transition"
              style={{ border: "1px solid #e2e8f0" }}
              rows={3}
              data-testid="consulting-checklist-item-notes-input"
            />
          )}
          {open && item.tablero && (
            <span className="block mt-1 text-[11px] font-semibold" style={{ color: "#7A2E20" }} data-testid="consulting-checklist-item-notes-privacy">
              Escribe funciones, roles o situaciones, nunca nombres de personas.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
